import { useEffect, useState, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Check, X, HelpCircle, MapPin, Music2, Send, Download, Clock } from "lucide-react";
import api, { API, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const STATUS_PILL = {
  pending: "bg-amber-soft text-amber-deep",
  confirmed: "bg-sage-100 text-sage-700",
  declined: "bg-terracotta-50 text-terracotta-700",
  maybe: "bg-ivory-100 text-ink-600",
};

export default function BookingDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [b, setB] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);

  const refresh = async () => {
    try {
      const { data } = await api.get(`/bookings/${id}`);
      setB(data);
      const msgRes = await api.get(`/bookings/${id}/messages`);
      setMsgs(msgRes.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);

  const respond = async (response) => {
    setBusy(true);
    try {
      await api.post(`/bookings/${id}/respond`, { response, message: "" });
      toast.success(response === "accepted" ? `Added to your diary · booking ${id.slice(-6)}` : `Marked ${response}`);
      refresh();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const sendMsg = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    try {
      await api.post(`/bookings/${id}/messages`, { text });
      setText("");
      const msgRes = await api.get(`/bookings/${id}/messages`);
      setMsgs(msgRes.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const downloadIcs = async () => {
    try {
      const res = await fetch(`${API}/calendar/booking/${id}.ics`, { credentials: "include" });
      const text = await res.text();
      const blob = new Blob([text], { type: "text/calendar" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `amds-booking-${id}.ics`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download"); }
  };

  if (!b) return <div className="text-ink-500">Loading…</div>;

  const isMusician = b.musician_id === user.id;
  const canRespond = isMusician && (b.status === "pending" || b.status === "maybe");

  return (
    <div className="space-y-5" data-testid="booking-detail-page">
      <Link to="/bookings" className="btn-ghost text-sm w-fit" data-testid="back-to-bookings"><ArrowLeft size={16} /> All bookings</Link>

      <div className="card-base p-5 sm:p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="label-overline text-ink-500">{b.event_type.replace("_", " ")}</div>
            <h1 className="font-heading text-2xl font-bold tracking-tight mt-1">{b.ensemble_name || "AMDS booking"}</h1>
            <div className="text-sm text-ink-600 mt-1">
              {isMusician ? `From ${b.booker_name}` : `To ${b.musician_name}`}
            </div>
          </div>
          <span className={`pill ${STATUS_PILL[b.status] || ""}`} data-testid="booking-detail-status">{b.status}</span>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mt-5">
          <Detail icon={Clock} label="When">
            {new Date(b.start_at).toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
            {" – "}
            {new Date(b.end_at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
          </Detail>
          {b.venue && <Detail icon={MapPin} label="Venue">{b.venue}</Detail>}
          {b.repertoire && <Detail icon={Music2} label="Repertoire">{b.repertoire}</Detail>}
          {b.fee && <Detail icon={() => <span className="font-semibold text-sage-600">£</span>} label="Fee">{b.fee}</Detail>}
        </div>

        {b.notes && (
          <div className="mt-4 p-4 rounded-xl bg-ivory-100 text-sm text-ink-700">
            <div className="label-overline text-ink-500 mb-1">Notes</div>
            {b.notes}
          </div>
        )}

        {canRespond && (
          <div className="mt-5 grid grid-cols-3 gap-2">
            <button onClick={() => respond("accepted")} disabled={busy} className="btn-primary justify-center" data-testid="respond-accept-btn"><Check size={16} /> Accept</button>
            <button onClick={() => respond("maybe")} disabled={busy} className="btn-secondary justify-center" data-testid="respond-maybe-btn"><HelpCircle size={16} /> Maybe</button>
            <button onClick={() => respond("declined")} disabled={busy} className="rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-white px-4 py-2.5 font-medium inline-flex items-center justify-center gap-2" data-testid="respond-decline-btn"><X size={16} /> Decline</button>
          </div>
        )}

        {b.status === "confirmed" && (
          <div className="mt-5 flex flex-wrap gap-2">
            <button onClick={downloadIcs} className="btn-secondary text-sm" data-testid="booking-ics-btn"><Download size={16} /> Add to calendar</button>
          </div>
        )}
      </div>

      <section className="card-base p-5">
        <h2 className="font-heading font-semibold text-base mb-3">Messages</h2>
        <div className="space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="messages-list">
          {msgs.length === 0 && <div className="text-sm text-ink-500">No messages yet.</div>}
          {msgs.map((m) => {
            const mine = m.sender_id === user.id;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                <div className={`rounded-2xl px-3 py-2 max-w-[78%] text-sm ${mine ? "bg-sage-500 text-white rounded-br-sm" : "bg-ivory-100 text-ink-800 rounded-bl-sm"}`}>
                  {!mine && <div className="text-[10px] uppercase tracking-widest opacity-70 mb-0.5">{m.sender_name}</div>}
                  <div>{m.text}</div>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
        <form onSubmit={sendMsg} className="mt-3 flex gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} className="input-field flex-1" placeholder="Type a message…" data-testid="msg-input" />
          <button type="submit" className="btn-primary" data-testid="msg-send-btn"><Send size={16} /></button>
        </form>
      </section>
    </div>
  );
}

function Detail({ icon: Icon, label, children }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-xl bg-ivory-100/60">
      <div className="w-8 h-8 grid place-items-center rounded-lg bg-white text-sage-600 border border-ivory-200">
        <Icon size={16} />
      </div>
      <div>
        <div className="label-overline text-ink-500">{label}</div>
        <div className="text-sm text-ink-800 mt-0.5">{children}</div>
      </div>
    </div>
  );
}
